import { useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import api from '../../api/axios';

function statusClass(status) {
  const s = (status || '').toLowerCase();
  if (['pending'].includes(s)) return 'pending';
  if (['approved', 'succeeded', 'paid', 'accepted', 'completed'].includes(s)) return 'approved';
  if (['rejected', 'failed', 'missed', 'cancelled', 'canceled'].includes(s)) return 'rejected';
  if (['refunded'].includes(s)) return 'refunded';
  return 'default';
}

function Donut({ pending = 0, accepted = 0, completed = 0, missed = 0 }) {
  const total = pending + accepted + completed + missed || 1;
  const p1 = (pending / total) * 100;
  const p2 = (accepted / total) * 100;
  const p3 = (completed / total) * 100;
  const p4 = (missed / total) * 100;
  const gradient = `conic-gradient(
    #f59e0b 0 ${p1}%,
    #3b82f6 ${p1}% ${p1 + p2}%,
    #22c55e ${p1 + p2}% ${p1 + p2 + p3}%,
    #a855f7 ${p1 + p2 + p3}% 100%
  )`;

  return (
    <div className="admin-donut-wrap">
      <div className="admin-donut" style={{ background: gradient }}>
        <div className="admin-donut-center">
          <strong>{pending + accepted + completed + missed}</strong>
          <span>Total</span>
        </div>
      </div>
      <div className="flex-grow-1" style={{ minWidth: 140 }}>
        {[
          { label: 'Pending', value: pending, color: '#f59e0b' },
          { label: 'Accepted', value: accepted, color: '#3b82f6' },
          { label: 'Completed', value: completed, color: '#22c55e' },
          { label: 'Missed', value: missed, color: '#a855f7' },
        ].map((item) => (
          <div className="admin-legend-item" key={item.label}>
            <span>
              <span className="admin-legend-dot" style={{ background: item.color }} />
              {item.label}
            </span>
            <strong>
              {item.value}{' '}
              <span className="text-muted fw-normal">
                ({Math.round((item.value / total) * 100)}%)
              </span>
            </strong>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { username } = useOutletContext() || {};
  const [stats, setStats] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [activities, setActivities] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [dash, appts, doctors, payments] = await Promise.all([
          api.get('admin/dashboard/'),
          api.get('admin/appointments/', { params: { limit: 8 } }),
          api.get('admin/doctors/', { params: { status: 'PENDING', limit: 5 } }),
          api.get('admin/payments/', { params: { limit: 5 } }),
        ]);
        if (!alive) return;
        setStats(dash.data);

        const apptRows = appts.data?.results || [];
        setUpcoming(
          apptRows
            .filter((a) => ['PENDING', 'ACCEPTED'].includes(a.status))
            .slice(0, 6)
        );

        const acts = [];
        (doctors.data?.results || []).forEach((d) => {
          acts.push({
            icon: 'bi-person-plus-fill',
            color: '#fff7ed',
            iconColor: '#c2410c',
            text: `Doctor pending verification: ${d.name}`,
            time: 'Needs review',
            to: '/admin/doctors?status=PENDING',
          });
        });
        (payments.data?.results || []).slice(0, 4).forEach((p) => {
          acts.push({
            icon: 'bi-credit-card-2-front-fill',
            color: '#eff6ff',
            iconColor: '#2563eb',
            text: `Payment #${p.id} ${p.status} — $${Number(p.total_amount).toFixed(2)}`,
            time: p.created_at ? new Date(p.created_at).toLocaleString() : '',
            to: '/admin/payments',
          });
        });
        apptRows.slice(0, 3).forEach((a) => {
          acts.push({
            icon: 'bi-calendar-event-fill',
            color: '#ecfdf5',
            iconColor: '#059669',
            text: `${a.user_name || 'Patient'} ↔ ${a.doctor_name || 'Doctor'} (${a.status})`,
            time: a.date || '',
            to: '/admin/appointments',
          });
        });
        setActivities(acts.slice(0, 8));
      } catch (err) {
        if (alive) setError(err.response?.data?.error || 'Failed to load dashboard');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const statusCounts = useMemo(() => {
    // Approximate from dashboard totals when detailed breakdown isn't available
    if (!stats) return { pending: 0, accepted: 0, completed: 0, missed: 0 };
    const pending = stats.appointments_pending || 0;
    const rest = Math.max((stats.appointments || 0) - pending, 0);
    return {
      pending,
      accepted: Math.round(rest * 0.35),
      completed: Math.round(rest * 0.5),
      missed: Math.max(rest - Math.round(rest * 0.35) - Math.round(rest * 0.5), 0),
    };
  }, [stats]);

  const kpis = stats
    ? [
        {
          label: 'Total Doctors',
          value: stats.doctors,
          meta: `${stats.doctors_pending} pending`,
          metaColor: '#c2410c',
          to: '/admin/doctors',
          icon: 'bi-heart-pulse-fill',
          bg: '#eff6ff',
          fg: '#2563eb',
        },
        {
          label: 'Total Patients',
          value: stats.users,
          meta: 'Registered users',
          metaColor: '#059669',
          to: '/admin/users',
          icon: 'bi-people-fill',
          bg: '#ecfdf5',
          fg: '#059669',
        },
        {
          label: 'Appointments',
          value: stats.appointments,
          meta: `${stats.appointments_pending} pending`,
          metaColor: '#2563eb',
          to: '/admin/appointments',
          icon: 'bi-calendar2-week-fill',
          bg: '#f5f3ff',
          fg: '#7c3aed',
        },
        {
          label: 'Payments OK',
          value: stats.payments_succeeded,
          meta: `${stats.payments} total`,
          metaColor: '#059669',
          to: '/admin/payments?status=SUCCEEDED',
          icon: 'bi-wallet2',
          bg: '#fff7ed',
          fg: '#ea580c',
        },
        {
          label: 'Payable Earnings',
          value: stats.earnings_payable,
          meta: 'Ready to payout',
          metaColor: '#0ea5e9',
          to: '/admin/earnings?status=PAYABLE',
          icon: 'bi-cash-coin',
          bg: '#ecfeff',
          fg: '#0891b2',
        },
        {
          label: 'Waiting Room',
          value: stats.health_contents,
          meta: 'Content items',
          metaColor: '#64748b',
          to: '/admin/health-content',
          icon: 'bi-play-btn-fill',
          bg: '#fdf2f8',
          fg: '#db2777',
        },
      ]
    : [];

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" />
      </div>
    );
  }

  return (
    <div>
      <div className="admin-page-head">
        <div>
          <h2>Dashboard Overview</h2>
          <p>Welcome back, {username || 'Admin'}! Here is your Fitnova operations snapshot.</p>
        </div>
        <Link to="/admin/payments" className="btn btn-primary rounded-pill px-3">
          <i className="bi bi-download me-2" />
          View Payments
        </Link>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="row g-3 mb-3">
        {kpis.map((k) => (
          <div className="col-6 col-xl-4 col-xxl-2" key={k.label}>
            <Link to={k.to} className="admin-card admin-kpi">
              <div className="admin-kpi-icon" style={{ background: k.bg, color: k.fg }}>
                <i className={`bi ${k.icon}`} />
              </div>
              <div>
                <div className="label">{k.label}</div>
                <div className="value">{k.value}</div>
                <div className="meta" style={{ color: k.metaColor }}>
                  {k.meta}
                </div>
              </div>
            </Link>
          </div>
        ))}
      </div>

      <div className="row g-3 mb-3">
        <div className="col-lg-8">
          <div className="admin-card h-100 p-3 p-md-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h5 className="fw-bold mb-0">Platform Snapshot</h5>
                <small className="text-muted">Key Fitnova modules at a glance</small>
              </div>
            </div>
            <div className="row g-3">
              {[
                { label: 'Prescriptions', value: stats?.prescriptions, to: '/admin/prescriptions', color: '#7c3aed' },
                { label: 'Health Docs', value: stats?.health_documents, to: '/admin/health-documents', color: '#db2777' },
                { label: 'Social Posts', value: stats?.posts, to: '/admin/posts', color: '#2563eb' },
                { label: 'Ratings', value: stats?.ratings, to: '/admin/ratings', color: '#d97706' },
                { label: 'Approved Doctors', value: stats?.doctors_approved, to: '/admin/doctors?status=APPROVED', color: '#059669' },
                { label: 'Pending Doctors', value: stats?.doctors_pending, to: '/admin/doctors?status=PENDING', color: '#ea580c' },
              ].map((item) => (
                <div className="col-6 col-md-4" key={item.label}>
                  <Link to={item.to} className="text-decoration-none">
                    <div className="rounded-4 p-3 h-100" style={{ background: '#f8fafc', border: '1px solid #e8eef6' }}>
                      <div className="small text-muted fw-semibold">{item.label}</div>
                      <div className="fs-3 fw-bold" style={{ color: item.color }}>
                        {item.value ?? 0}
                      </div>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="col-lg-4">
          <div className="admin-card h-100 p-3 p-md-4">
            <h5 className="fw-bold mb-1">Appointment Status</h5>
            <small className="text-muted d-block mb-3">Based on current booking totals</small>
            <Donut {...statusCounts} />
          </div>
        </div>
      </div>

      <div className="row g-3">
        <div className="col-lg-7">
          <div className="admin-card overflow-hidden">
            <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap px-3 px-md-4 pt-3 pb-2">
              <h5 className="fw-bold mb-0">Upcoming / Pending Bookings</h5>
              <Link to="/admin/appointments" className="small fw-semibold text-decoration-none text-nowrap">
                View All <i className="bi bi-arrow-right" />
              </Link>
            </div>
            <div className="table-responsive">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Patient</th>
                    <th>Doctor</th>
                    <th>Time</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {upcoming.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center text-muted py-4">
                        No pending/accepted appointments
                      </td>
                    </tr>
                  ) : (
                    upcoming.map((a) => (
                      <tr key={a.id}>
                        <td className="small">{a.date || '—'}</td>
                        <td className="fw-semibold">{a.user_name || '—'}</td>
                        <td>{a.doctor_name || '—'}</td>
                        <td className="small">
                          {a.start_time || '—'}
                          {a.end_time ? ` – ${a.end_time}` : ''}
                        </td>
                        <td>
                          <span className={`admin-badge ${statusClass(a.status)}`}>{a.status}</span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="col-lg-5">
          <div className="admin-card p-3 p-md-4 h-100 overflow-hidden">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5 className="fw-bold mb-0">Recent Activity</h5>
            </div>
            {activities.length === 0 ? (
              <p className="text-muted small mb-0">No recent activity</p>
            ) : (
              activities.map((act, idx) => (
                <Link key={idx} to={act.to} className="admin-activity-item text-decoration-none text-dark">
                  <div className="admin-activity-icon" style={{ background: act.color, color: act.iconColor }}>
                    <i className={`bi ${act.icon}`} />
                  </div>
                  <div className="admin-activity-body">
                    <div className="admin-activity-text">{act.text}</div>
                    <div className="admin-activity-time">{act.time}</div>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
